import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import {
  resolveAiFrameworks,
  getSoa,
  getCrossFrameworkOpportunity,
} from './aiGovApi';

/** A4 (readiness) + B3 (crosswalk reuse) overview, scoped to the two AI frameworks.
 *  Readiness is derived from the SoA endpoint (always complete, independent of test-gated
 *  enrollment); crosswalk reuse reuses the existing /api/compliance engine. */
export default function AiGovOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState([]);
  const [opportunity, setOpportunity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('loading'); // 'ok' | 'not-enrolled' | 'error'

  useEffect(() => {
    let alive = true;
    // Resolve the org's REAL AI framework ids by name, then load their SoAs. This is what
    // makes the workspace robust across orgs/envs (no hardcoded ids) and lets us show a
    // precise empty/error state instead of a blank "not accessible" page.
    resolveAiFrameworks()
      .then(async (aiFws) => {
        if (!aiFws.length) { if (alive) { setStatus('not-enrolled'); setLoading(false); } return; }
        // Render readiness immediately from the fast per-framework SoA calls (<200ms each).
        const soas = await Promise.all(aiFws.map((f) => getSoa(f.id).catch(() => null)));
        if (!alive) return;
        const ids = aiFws.map((f) => f.id);
        const resolved = aiFws.map((f, i) => ({ framework: f, soa: soas[i] })).filter((x) => x.soa);
        setFrameworks(resolved);
        setStatus(resolved.length ? 'ok' : 'not-enrolled');
        setLoading(false);
        // Enrich with the cross-framework reuse metric WITHOUT blocking the page — this
        // endpoint can take 10s+ on large orgs, and the workspace must not spin waiting on
        // a secondary number (this was the "AI Governance not accessible" symptom).
        getCrossFrameworkOpportunity()
          .then((opp) => { if (alive) setOpportunity((opp || []).filter((o) => ids.includes(o.frameworkId))); })
          .catch(() => {});
      })
      .catch(() => { if (alive) { setStatus('error'); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const reuseFor = (id) => opportunity.find((o) => o.frameworkId === id);

  const cls = (pct) => (pct >= 80 ? 'aig-val-ok' : pct >= 50 ? 'aig-val-warn' : 'aig-val-bad');

  if (loading) return <div className="aig-page"><div className="aig-spinner" /></div>;

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.title', 'AI Governance')}</h1>
        <p className="aig-subtitle">
          {t('AIGovernance.subtitle', 'ISO/IEC 42001 & NIST AI RMF readiness, AI asset inventory and audit-ready artifacts.')}
        </p>
      </div>

      <AiGovNav />

      {status === 'error' ? (
        <div className="aig-empty">
          {t('AIGovernance.loadError', 'Could not load AI Governance. Your session may have expired — try reloading or signing in again.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aig-btn aig-btn-primary" onClick={() => window.location.reload()}>
              {t('AIGovernance.reload', 'Reload')}
            </button>
          </div>
        </div>
      ) : frameworks.length === 0 ? (
        <div className="aig-empty">
          {t('AIGovernance.notEnrolled', 'Your organization has not enrolled in an AI framework yet. Enrol in ISO/IEC 42001 or NIST AI RMF from the Frameworks page to activate the AI Governance workspace.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aig-btn aig-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('AIGovernance.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      ) : (
        <div className="aig-grid">
          {frameworks.map(({ framework, soa }) => {
            const reuse = reuseFor(framework.id);
            const readiness = soa.readinessPct || 0;
            return (
              <div className="aig-card" key={framework.id}>
                <h3 className="aig-card-title">{soa.frameworkName || framework.displayName}</h3>
                <div className="aig-bar">
                  <div className="aig-bar-pass" style={{ width: `${readiness}%` }} />
                </div>
                <div className="aig-card-row">
                  <span>{t('AIGovernance.readiness', 'Readiness')}</span>
                  <strong className={cls(readiness)}>{readiness}%</strong>
                </div>
                <div className="aig-card-row"><span>{t('AIGovernance.implemented', 'Implemented')}</span><span>{soa.implementedCount} / {soa.applicableCount}</span></div>
                <div className="aig-card-row"><span>{t('AIGovernance.controls', 'Controls')}</span><span>{soa.totalControls}</span></div>
                <div className="aig-card-row"><span>{t('AIGovernance.excluded', 'Excluded')}</span><span>{soa.excludedCount}</span></div>
                {reuse && (
                  <div className="aig-card-row">
                    <span>{t('AIGovernance.evidenceReuse', 'Evidence reuse')}</span>
                    <strong className={cls(reuse.coveragePercent || 0)}>{reuse.coveragePercent || 0}%</strong>
                  </div>
                )}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button className="aig-btn" onClick={() => navigate(`/dashboard/ai-governance/soa?frameworkId=${framework.id}`)}>
                    {t('AIGovernance.viewSoa', 'View SoA')}
                  </button>
                  <button className="aig-btn" onClick={() => navigate('/dashboard/compliance/hub')}>
                    {t('AIGovernance.viewCrosswalk', 'Crosswalk')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
